<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: Dialog that adds one signer to a signing process through the dedicated endpoint — name, profile role, order, channel variant and the delivery addresses captured together with the person anchor they belong to.
-->
<p-dialog header="Unterzeichner hinzufügen"
          [visible]="visible"
          [modal]="true"
          [draggable]="false"
          [resizable]="false"
          [closable]="!saving"
          [style]="{'width': '44rem', 'max-width': '95vw'}"
          (visibleChange)="!$event && cancel()">

    <div class="flex flex-column gap-3">

        <!-- Name -->
        <div>
            <label class="block font-medium mb-2" for="smSignerName">
                Name der unterzeichnenden Person *
            </label>
            <input id="smSignerName"
                   type="text"
                   pInputText
                   class="w-full"
                   [(ngModel)]="signerName"
                   [disabled]="saving"
                   placeholder="Vor- und Nachname"/>
        </div>

        <!--
          The role is a matching TOKEN, not a label: it is compared verbatim against the part of the
          signature field name in front of the separator. Offered from the profile so that what is
          sent can actually match something in the document.
        -->
        <div>
            <label class="block font-medium mb-2" for="smSignerRole">Rolle</label>

            @if (rolesLoading) {
                <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem"></p-skeleton>
            } @else if (roleIsFreeText) {
                <input id="smSignerRole"
                       type="text"
                       pInputText
                       class="w-full"
                       [(ngModel)]="signerRole"
                       [disabled]="saving"
                       placeholder="Rollenschlüssel exakt wie im Dokument"/>

                @if (rolesLoadError) {
                    <p-message severity="warn"
                               [text]="rolesLoadError"
                               styleClass="w-full mt-2">
                    </p-message>
                } @else if (!signatureProfileId) {
                    <span class="block text-sm text-color-secondary mt-2">
                        Für diesen Signaturprozess ist kein Signaturprofil hinterlegt, daher steht
                        keine Rollenliste zur Verfügung. Der Schlüssel wird zeichengenau mit dem
                        Dokument verglichen — Groß- und Kleinschreibung sowie Umlaute zählen.
                    </span>
                } @else {
                    <span class="block text-sm text-color-secondary mt-2">
                        Für dieses Signaturprofil ist keine Rolle konfiguriert. Der Schlüssel wird
                        zeichengenau mit dem Dokument verglichen — Groß- und Kleinschreibung sowie
                        Umlaute zählen.
                    </span>
                }
            } @else {
                <p-select inputId="smSignerRole"
                          [options]="roles"
                          [(ngModel)]="signerRole"
                          optionValue="roleKey"
                          [showClear]="true"
                          [disabled]="saving"
                          appendTo="body"
                          placeholder="Rolle wählen"
                          styleClass="w-full">
                    <ng-template pTemplate="item" let-role>
                        <div class="flex flex-column gap-1 py-1">
                            <span class="font-medium">{{ roleLabel(role) }}</span>
                            <span class="text-sm text-color-secondary">{{ role.roleKey }}</span>
                        </div>
                    </ng-template>
                </p-select>
            }
        </div>

        <div class="grid">
            <div class="col-12 md:col-6">
                <label class="block font-medium mb-2" for="smSignerOrder">Reihenfolge</label>
                <p-inputNumber inputId="smSignerOrder"
                               [(ngModel)]="signingOrder"
                               [min]="1"
                               [showButtons]="true"
                               [disabled]="saving"
                               styleClass="w-full">
                </p-inputNumber>
                <span class="block text-sm text-color-secondary mt-2">
                    Bei sequenzieller Signaturordnung wird nur die niedrigste Nummer eingeladen; die
                    übrigen folgen, sobald diese unterschrieben hat.
                </span>
            </div>
            <div class="col-12 md:col-6">
                <label class="block font-medium mb-2">Pflichtunterschrift</label>
                <div class="flex align-items-center gap-2">
                    <p-toggleSwitch [(ngModel)]="mandatory" [disabled]="saving"></p-toggleSwitch>
                    <span class="text-sm">
                        {{ mandatory ? 'Muss unterschreiben' : 'Kann unterschreiben' }}
                    </span>
                </div>
            </div>
        </div>

        <!--
          The variant decides the ROUTE the invitation takes, and with it what the address capture
          below has to ask for. Only the variants the signature profile releases are offered — an
          unreleased one is refused by the backend by name.
        -->
        <div>
            <mvs-sm-channel-variant-select
                [signatureProfileId]="signatureProfileId"
                [disabled]="saving"
                (onChannelTypeChanged)="handleChannelTypeChanged($event)">
            </mvs-sm-channel-variant-select>
        </div>

        <!--
          The addresses, and WHOSE they are. Not two plain inputs: an address alone is a destination,
          and a check against one would only confirm that we delivered to what somebody typed. The
          capture produces the address, the PERSON it belongs to, and the ORIGIN of that claim — and
          the origin is derived server-side from the anchor, never sent from here.

          Only where a variant was chosen. A signer with no variant signs in the room and has no
          delivery route at all; asking for a delivery address there would ask for something the
          process will never use.
        -->
        @if (selectedChannelType) {
            <div>
                <mvs-sm-signer-address-capture
                    [channelType]="selectedChannelType"
                    [personObjectTypeAlias]="personObjectTypeAlias"
                    [disabled]="saving"
                    (onCaptureChanged)="handleCaptureChanged($event)">
                </mvs-sm-signer-address-capture>
            </div>
        } @else {
            <p-message severity="info"
                       text="Ohne Kanalvariante wird kein Zustellweg hinterlegt. Diese Person kann dann nur unmittelbar vor Ort unterschreiben und wird beim Versand nicht berücksichtigt."
                       styleClass="w-full">
            </p-message>
        }

        <!--
          The server's own sentence, verbatim. It names which rule refused — ein abgeschlossener
          Prozess, eine nicht freigegebene Kanalvariante, ein fehlender Name — and which of those it
          was is nothing this dialog can know.
        -->
        @if (saveError) {
            <p-message severity="error" [text]="saveError" styleClass="w-full"></p-message>
        }
    </div>

    <ng-template pTemplate="footer">
        <p-button label="Abbrechen"
                  icon="fa-regular fa-xmark"
                  severity="secondary"
                  [text]="true"
                  [disabled]="saving"
                  (onClick)="cancel()">
        </p-button>
        <p-button label="Unterzeichner anlegen"
                  icon="fa-regular fa-user-plus"
                  [loading]="saving"
                  [disabled]="!submittable"
                  (onClick)="save()">
        </p-button>
    </ng-template>
</p-dialog>
